import React, { useCallback, useEffect, useMemo, useState } from 'react';
import debounce from 'lodash/debounce';
import { useGetAllFullscriptAllergensLazyQuery } from 'graphql/generated/remote-schema-hasura';
import { SelectOption } from 'components/stepper/types';
import {
  FormMultiSelect,
  FormSelect,
} from 'components/stepper/field-components';
import { ReactComponent as DeleteIcon } from 'assets/icons/delete.svg';
import { ReactComponent as PlusIcon } from 'assets/icons/plusIcon.svg';
import {
  FieldValues,
  Path,
  useController,
  useFieldArray,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import { CustomFieldProps } from 'components/stepper/field-components/QuestionnaireField';
import { DEFAULT_NONE_OPTION } from 'components/stepper/field-components/FormMultiSelect';
import { isArray } from 'lodash';
import ButtonComponent from 'components/button/buttonComponent';
import { twMerge } from 'tailwind-merge';
import { SingleValue } from 'react-select';
import { MedAllergens } from '../useQuestionaireValidations';
import { useGetAllergensClasses } from './useGetAllergensClasses';
import { isValidNdc } from 'utilities/questionnaireHelpers';

interface FormAllergensFieldProps<TFieldValues> {
  name: Path<TFieldValues>;
  label: string;
  placeholder?: string;
  isRequired?: boolean;
  describe?: string;
  isDisabled?: boolean;
}

interface FormMedAllergensFieldProps<TFieldValues>
  extends FormAllergensFieldProps<TFieldValues> {
  labelFieldName: Path<TFieldValues>;
  index: number;
}

type MedAllergenItem = MedAllergens & {
  id: string;
};

const FormAllergensField = <TFieldValues extends Record<string, string>>({
  name,
  label,
  placeholder,
  isRequired,
  describe,
  isDisabled,
}: FormAllergensFieldProps<TFieldValues>) => {
  const [options, setOptions] = useState<SelectOption[]>([]);
  const [search, setSearch] = useState('');
  const [valueOptions, setValuesOptions] = useState<SelectOption[]>([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  const LIMIT = 20;

  const { control } = useFormContext<TFieldValues>();
  const { field } = useController({ name, control });

  const mapOptions = (incoming: string[]) => {
    const map = new Map<string, SelectOption>();

    [
      ...incoming,
      ...(options || []).map((o) => o.value),
      ...(valueOptions || []).map((o) => o.value),
    ]
      .filter(Boolean)
      .forEach((val) => map.set(String(val), { value: val, label: val }));

    return Array.from(map.values());
  };

  const [fetchAllergens, { loading }] = useGetAllFullscriptAllergensLazyQuery({
    onCompleted: (data) => {
      const newItems = data.getFullscriptAllergens.fullscript_allergens.map(
        (a) => a.fullscript_name,
      );

      if (newItems.length < LIMIT) setHasMore(false);

      setOptions((prev) =>
        mapOptions([...prev.map((p) => p.value), ...newItems]),
      );
    },
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedFetch = useCallback(debounce(fetchAllergens, 600), []);

  const allOptions = useMemo(() => {
    const valueOptionIsNone = field.value?.[0] === DEFAULT_NONE_OPTION;

    const map = new Map<string, SelectOption>();

    valueOptions?.forEach((opt) => map.set(opt.value, opt));

    options?.forEach((opt) => map.set(opt.value, opt));

    const optionsArray = Array.from(map.values());

    if (search || valueOptionIsNone) return optionsArray;

    return [
      {
        label: DEFAULT_NONE_OPTION,
        value: DEFAULT_NONE_OPTION,
      },
      ...optionsArray,
    ];
  }, [field.value, options, search, valueOptions]);

  const loadMore = () => {
    if (!hasMore || loading) return;

    const nextOffset = offset + LIMIT;
    setOffset(nextOffset);

    fetchAllergens({
      variables: { search, limit: LIMIT, offset: nextOffset },
    });
  };

  useEffect(() => {
    setOffset(0);
    setOptions([]);
    setHasMore(true);

    debouncedFetch({
      variables: { search, limit: LIMIT, offset: 0 },
    });
  }, [search, debouncedFetch]);

  useEffect(() => {
    const newValueOption = (isArray(field.value) ? field.value : [])?.map(
      (item) => {
        return {
          value: item,
          label: item,
        };
      },
    );

    setValuesOptions(newValueOption);
  }, [field.value]);

  useEffect(() => {
    debouncedFetch({
      variables: {
        search,
        limit: LIMIT,
      },
    });
  }, [search, debouncedFetch]);

  return (
    <>
      <FormMultiSelect
        name={name}
        label={label}
        placeholder={placeholder}
        isRequired={isRequired}
        describe={describe}
        options={allOptions}
        isDisabled={isDisabled}
        isSearchable={true}
        isLoading={loading}
        onInputChange={(value: string) => setSearch(value)}
        onMenuScrollToBottom={loadMore}
      />
    </>
  );
};

const MedFormAllergensField = <TFieldValues extends Record<string, string>>({
  name,
  label,
  placeholder,
  isRequired,
  describe,
  isDisabled,
  labelFieldName,
  index,
}: FormMedAllergensFieldProps<TFieldValues>) => {
  const {
    medicationsList,
    handleLoadMoreMedications,
    loading,
    isFetchingMore,
    handleSearchChange,
  } = useGetAllergensClasses();

  const { control, setValue, watch, getValues } = useFormContext();
  const { field } = useController({ name, control });

  const v = watch(name);
  const [selected, setSelected] = useState<SelectOption[]>(() => {
    const labels = getValues().medAllergens;
    if (!Array.isArray(labels)) return [];
    return labels
      .filter(({ medication }) => !isValidNdc(medication))
      .map(({ label, medication }) => ({
        label,
        value: medication,
      }));
  });

  const options = useMemo(
    () =>
      [
        !index && {
          label: DEFAULT_NONE_OPTION,
          value: DEFAULT_NONE_OPTION,
        },
        ...medicationsList,
      ].filter(Boolean) as SelectOption[],
    [index, medicationsList],
  );

  useEffect(() => {
    if (Array.isArray(v)) {
      setSelected((prev) => {
        const set = new Set([...prev.map(({ value }) => value), ...v]);
        return [...set].map((val) => ({
          value: val,
          label:
            [...options, ...prev].find(({ value }) => value === val)?.label ??
            '',
        }));
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [v]);

  const allOptions = useMemo(() => {
    const map = new Map<string, SelectOption>();

    options?.forEach((opt) => {
      if (opt.value) map.set(opt.value, opt);
    });

    selected?.forEach((opt) => {
      if (opt.value && !map.has(opt.value)) {
        map.set(opt.value, opt);
      }
    });

    return Array.from(map.values());
  }, [options, selected]);

  const handleChange = (newValue: SingleValue<SelectOption>) => {
    if (newValue?.value === DEFAULT_NONE_OPTION) {
      return setValue('medAllergens', [
        {
          medication: newValue.value,
          label: newValue.value,
          reaction: undefined,
        },
      ]);
    }
    field.onChange(newValue ? newValue.value : null);
    setValue(labelFieldName, (newValue ? newValue.label : '') as never, {
      shouldTouch: true,
    });
  };

  return (
    <FormSelect
      name={name}
      label={label}
      placeholder={placeholder}
      isRequired={isRequired}
      describe={describe}
      options={allOptions}
      onChange={handleChange as never}
      withoutTouch={false}
      isDisabled={isDisabled || loading || isFetchingMore}
      isSearchable
      isLoading={loading || isFetchingMore}
      onInputChange={handleSearchChange}
      onMenuScrollToBottom={handleLoadMoreMedications}
    />
  );
};

export const AllergensQuestionnaireField = <TFormValues extends FieldValues>({
  question,
  isRequired,
  isDisabled,
}: CustomFieldProps) => {
  return (
    <FormAllergensField<TFormValues>
      name={String(question.id) as Path<TFormValues>}
      label={(question.text ?? '') as string}
      placeholder={question.placeholder ?? 'Select allergens'}
      isRequired={isRequired}
      describe={
        question.helper_text ||
        'Select “None”, if you have not had an any allergies.'
      }
      isDisabled={isDisabled}
    />
  );
};

export const MedAllergensQuestionnaireField = <
  TFormValues extends FieldValues,
>({
  question,
  isDisabled,
}: CustomFieldProps) => {
  const { control } = useFormContext();

  const allergensOptions = useMemo(
    () =>
      (question.answers ?? []).map((value) => ({
        value,
        label: value,
      })),
    [question],
  );

  const allOptions = useMemo(
    () => [
      // {
      //   label: DEFAULT_NONE_OPTION,
      //   value: DEFAULT_NONE_OPTION,
      // },
      ...allergensOptions,
    ],
    [allergensOptions],
  );

  const { fields, remove, append } = useFieldArray({
    control,
    name: 'medAllergens',
  });

  const w = useWatch({
    control,
    name: 'medAllergens',
  });
  const isNone = w?.some(
    (item: Record<string, string>) => item?.medication === DEFAULT_NONE_OPTION,
  );

  const handleAddAllergen = async () => {
    append({
      label: undefined,
      medication: undefined,
      reaction: [],
    });
  };

  return (
    <div className="flex flex-col gap-5 w-[calc(100%_+_110px)]">
      {fields.map((item, i, array) => {
        const field = item as unknown as MedAllergenItem;
        return (
          <div className="flex flex-col desktop:flex-row desktop:gap-5 gap-2.5">
            <MedFormAllergensField<TFormValues>
              key={`medication-${field.id}`}
              name={`medAllergens.${i}.medication` as Path<TFormValues>}
              labelFieldName={`medAllergens.${i}.label` as Path<TFormValues>}
              label={(question.text ?? '') as string}
              placeholder={'Select allergens'}
              isRequired
              describe={
                question.helper_text ||
                'Select “None”, if you have not had an any'
              }
              index={i}
              isDisabled={isDisabled}
            />

            <FormMultiSelect
              name={`medAllergens.${i}.reaction` as Path<TFormValues>}
              key={`reaction-${field.id}`}
              label={'Allergic reactions'}
              className="w-full gap-0.5"
              options={allOptions}
              disabled={isNone}
              describe={
                question.helper_text ||
                'Select “None”, if you have not had an any'
              }
              isRequired
            />

            {
              <ButtonComponent
                type="underline"
                className={twMerge(
                  'ml-auto my-auto h-fit w-[90px] invisible',
                  array.length > 1 && 'visible',
                )}
                testID={`${i}-remove`}
                onClick={() => remove(i)}
                Icon={DeleteIcon}
                htmlButtonType="button"
                iconPosition="left"
              >
                Remove
              </ButtonComponent>
            }
          </div>
        );
      })}
      <ButtonComponent
        type="underline"
        testID={'add-medication'}
        onClick={handleAddAllergen}
        iconPosition="left"
        className="mr-auto"
        htmlButtonType="button"
        disabled={isNone}
        Icon={PlusIcon}
      >
        Add another medication
      </ButtonComponent>
    </div>
  );
};
